import React, { useState } from 'react';
import { Box, Container, Typography, Button, Tabs, Tab, Alert, Card, CardContent, Grid } from '@mui/material';
import { Assignment as AssignmentIcon, Email as EmailIcon, Warning as WarningIcon } from '@mui/icons-material';
import DriverApprovalRequestList from './DriverApprovalRequestList';
import DriverApprovalRequestForm from './DriverApprovalRequestForm';

const DriverApprovalRequestsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCreateNew = () => {
    setSelectedRequest(null);
    setFormOpen(true);
  };

  const handleEditRequest = (request) => {
    setSelectedRequest(request);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    setSelectedRequest(null);
    // Refresh the list without page reload
    // The parent component should handle the refetch
  };

  const tabLabels = [
    { label: 'All Requests', value: 0 },
    { label: 'Pending', value: 1 },
    { label: 'Approved', value: 2 },
    { label: 'Rejected', value: 3 }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h3" component="h1">
            Driver Approval Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AssignmentIcon />}
            onClick={handleCreateNew}
          >
            New Driver Request
          </Button>
        </Box>

        {/* Workflow Information Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Driver Approval Workflow
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon color="primary" />
                  <Typography variant="body2">
                    <strong>1. Select Employee & Policy</strong><br />
                    Choose employee and insurance policy from same business unit
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon color="primary" />
                  <Typography variant="body2">
                    <strong>2. Send Email Request</strong><br />
                    Email sent to insurance agent with driver's license photo
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="body2">
                    <strong>3. Monitor Response</strong><br />
                    Check Gmail for agent response and update status
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon color="success" />
                  <Typography variant="body2">
                    <strong>4. Update Authorization</strong><br />
                    Approved drivers can operate covered vehicles
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Important Notes */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Important Notes:
          </Typography>
          <Typography variant="body2" component="div">
            • Only employees from the same business unit as the insurance policy can be requested<br />
            • Driver's license photo must be uploaded to employee record before approval request<br />
            • All emails are sent through your connected Gmail account<br />
            • You must manually monitor Gmail responses and update request statuses
          </Typography>
        </Alert>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {tabLabels.map((tab) => (
              <Tab key={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && (
            <DriverApprovalRequestList 
              onEdit={handleEditRequest}
            />
          )}
          {activeTab === 1 && (
            <DriverApprovalRequestList 
              statusFilter="pending"
              onEdit={handleEditRequest}
            />
          )}
          {activeTab === 2 && (
            <DriverApprovalRequestList 
              statusFilter="approved"
              onEdit={handleEditRequest}
            />
          )}
          {activeTab === 3 && (
            <DriverApprovalRequestList 
              statusFilter="rejected"
              onEdit={handleEditRequest}
            />
          )}
        </Box>

        <DriverApprovalRequestForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          request={selectedRequest}
          onSuccess={handleFormSuccess}
        />
      </Box>
    </Container>
  );
};

export default DriverApprovalRequestsPage;
